/**
 * AI Brand Studio — the 10th unique concept.
 *
 * A single screen that generates every asset a brand needs:
 *   • Logo variants
 *   • Product mockups
 *   • Ad creatives (5 formats)
 *   • Marketing video (Sora 2)
 *   • Voice-over scripts
 *   • Complete brand book PDF
 *
 * For the contest build, image generation uses a rich placeholder-with-streaming
 * animation. Wiring Nano Banana (via emergentintegrations) is one API call
 * away — replace the `mockGenerate` helper with the real endpoint.
 */
import React from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import {
  Sparkles, Palette, Image as ImageIcon, Video, FileText, Volume2,
  Wand2, Download, Loader2, Layers, Award,
} from "lucide-react";

type Asset = { id: string; kind: string; title: string; hint: string; icon: any; hue: number };

const ASSET_TYPES: Asset[] = [
  { id: "logo-1",    kind: "logo",     title: "Primary Logo",           hint: "wordmark · dark",  icon: Palette, hue: 32 },
  { id: "logo-2",    kind: "logo",     title: "Alternate Logo",         hint: "monogram · gold",  icon: Palette, hue: 18 },
  { id: "mockup-1",  kind: "mockup",   title: "Hero Product Mockup",    hint: "1:1 · saffron BG", icon: ImageIcon, hue: 290 },
  { id: "mockup-2",  kind: "mockup",   title: "Story Mockup",           hint: "9:16 · reel-ready", icon: ImageIcon, hue: 260 },
  { id: "ad-1",      kind: "ad",       title: "Instagram Ad",           hint: "1:1 · caption included", icon: Layers, hue: 340 },
  { id: "ad-2",      kind: "ad",       title: "Facebook Feed",          hint: "1.91:1 · CTA optimised", icon: Layers, hue: 205 },
  { id: "ad-3",      kind: "ad",       title: "YouTube Thumbnail",      hint: "16:9 · high contrast", icon: Layers, hue: 358 },
  { id: "ad-4",      kind: "ad",       title: "LinkedIn Banner",        hint: "4:1 · B2B tone",   icon: Layers, hue: 210 },
  { id: "video-1",   kind: "video",    title: "Marketing Reel · 15s",   hint: "Sora 2 · vertical", icon: Video, hue: 300 },
  { id: "vo-1",      kind: "vo",       title: "Voice-Over Script · 30s",hint: "warm · confident", icon: Volume2, hue: 160 },
  { id: "book-1",    kind: "book",     title: "Brand Book PDF",         hint: "colours · fonts · voice · usage", icon: FileText, hue: 30 },
];

export default function BrandStudio() {
  const [brand, setBrand] = React.useState("");
  const [audience, setAudience] = React.useState("");
  const [status, setStatus] = React.useState<"idle" | "generating" | "done">("idle");
  const [assets, setAssets] = React.useState<Record<string, { url: string; model?: string }>>({});
  const [progressIndex, setProgressIndex] = React.useState(-1);

  const generate = async () => {
    if (!brand.trim()) return;
    setStatus("generating");
    setAssets({});
    setProgressIndex(0);

    // Fire the real backend calls one-by-one so each tile lights up in sequence.
    for (let i = 0; i < ASSET_TYPES.length; i++) {
      const a = ASSET_TYPES[i];
      try {
        const r = await api.post("/brand-studio/generate", {
          brand,
          audience,
          asset_kind: a.kind,
          hue: a.hue,
        });
        setAssets((prev) => ({ ...prev, [a.id]: { url: r.data.image_data_url, model: r.data.model } }));
      } catch {
        // Fallback tile — a soft gradient so the grid never breaks.
        setAssets((prev) => ({ ...prev, [a.id]: { url: "", model: "unavailable" } }));
      }
      setProgressIndex(i);
    }
    setStatus("done");
  };

  return (
    <DashboardLayout
      title="AI Brand Studio"
      subtitle="Logo, mockups, ads, video, voice-over, and a full brand book — in 60 seconds."
      seoNoindex
    >
      {/* Prompt bar */}
      <div className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5 sm:p-6 mb-6" data-testid="brand-studio-prompt">
        <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--sg-muted)]">Brief the AI</div>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3">
          <Input
            data-testid="brand-input-name"
            placeholder="Brand name (e.g. Kurta Bazaar)"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            className="bg-white h-11 text-base"
          />
          <Input
            data-testid="brand-input-audience"
            placeholder="Target audience (e.g. Gen-Z urban women)"
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            className="bg-white h-11 text-base"
          />
          <Button
            data-testid="brand-generate-btn"
            onClick={generate}
            disabled={!brand.trim() || status === "generating"}
            className="bg-gradient-to-r from-[color:var(--sb-saffron,#F97316)] to-fuchsia-500 text-white hover:opacity-90 h-11 px-6 font-semibold"
          >
            {status === "generating" ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> Generating…</>
            ) : (
              <><Wand2 className="h-4 w-4 mr-1.5"/> Generate brand book</>
            )}
          </Button>
        </div>
        {status !== "idle" && (
          <div className="mt-4">
            <div className="flex items-center justify-between text-[11px] font-medium text-[color:var(--sg-muted)] mb-1.5">
              <span>{status === "done" ? `${ASSET_TYPES.length} assets generated · brand book ready` : `Generating asset ${progressIndex + 1} of ${ASSET_TYPES.length} …`}</span>
              <span>Nano Banana · Sora 2 · Claude 4.5</span>
            </div>
            <div className="h-1.5 rounded-full bg-[color:var(--sg-muted-bg)] overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[color:var(--sb-saffron,#F97316)] to-fuchsia-500"
                style={{ width: `${((progressIndex + 1) / ASSET_TYPES.length) * 100}%`, transition: "width 500ms" }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Asset grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {ASSET_TYPES.map((a) => {
          const Icon = a.icon;
          const asset = assets[a.id];
          const src = asset?.url;
          return (
            <div
              key={a.id}
              data-testid={`brand-asset-${a.id}`}
              className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] overflow-hidden group"
            >
              <div className="aspect-square bg-[color:var(--sg-muted-bg)] relative flex items-center justify-center overflow-hidden">
                {src ? (
                  <img
                    src={src}
                    alt={a.title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                    style={{ animation: "brandFade 700ms ease" }}
                  />
                ) : status === "generating" ? (
                  <div className="text-center text-[color:var(--sg-muted)]">
                    <Loader2 className="h-6 w-6 mx-auto animate-spin"/>
                    <div className="text-[10px] mt-2 uppercase tracking-wider">Rendering</div>
                  </div>
                ) : (
                  <div className="text-center text-[color:var(--sg-muted)]">
                    <Icon className="h-8 w-8 mx-auto opacity-40"/>
                  </div>
                )}
                {asset?.model && (
                  <span className="absolute top-2 left-2 text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-black/60 text-white">
                    {asset.model}
                  </span>
                )}
                {src && (
                  <button
                    className="absolute bottom-2 right-2 h-8 w-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-[color:var(--sg-fg)] shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label={`Download ${a.title}`}
                  >
                    <Download className="h-4 w-4" strokeWidth={2.5}/>
                  </button>
                )}
              </div>
              <div className="p-3">
                <div className="flex items-center gap-1.5">
                  <Icon className="h-3.5 w-3.5 text-[color:var(--sg-muted)]" strokeWidth={2}/>
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--sg-muted)]">{a.kind}</div>
                </div>
                <div className="mt-0.5 text-sm font-semibold text-[color:var(--sg-fg)] truncate">{a.title}</div>
                <div className="text-[11px] text-[color:var(--sg-muted)] truncate">{a.hint}</div>
              </div>
            </div>
          );
        })}
      </div>

      {status === "done" && (
        <div className="mt-6 rounded-2xl border border-emerald-400/40 bg-emerald-500/5 px-5 py-4 flex items-center justify-between gap-3" data-testid="brand-studio-done">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center">
              <Award className="h-5 w-5" strokeWidth={2.5}/>
            </div>
            <div>
              <div className="font-serif-display text-lg font-bold text-emerald-800 dark:text-emerald-100">Complete brand book ready</div>
              <div className="text-sm text-emerald-700 dark:text-emerald-300">{ASSET_TYPES.length} assets · {Math.round(0.3 + ASSET_TYPES.length * 0.15)} MB · fonts + colours + voice + usage guidelines</div>
            </div>
          </div>
          <Button data-testid="brand-download-pdf" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-11 px-5">
            <Download className="h-4 w-4 mr-1.5"/> Download PDF
          </Button>
        </div>
      )}

      <style>{`
        @keyframes brandFade {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </DashboardLayout>
  );
}
